import { useEffect, useState } from "react";
import type { ThreadProviderRateLimitStatus } from "@cmmd-forge/contracts";
import type { CmmdProviderAccountInventoryAccount } from "@cmmd-forge/contracts";
import {
  nextRateLimitResetMs,
  selectRateLimitBannerWindow,
  type RateLimitBannerWindow,
} from "./rateLimitBannerSelection";

/**
 * The window the banner should describe, re-evaluated exactly when it lapses.
 *
 * The timer is the point: nothing else wakes this component when a limit
 * expires. The provider only reports rate limits during a turn, which is
 * precisely what the user is NOT doing while waiting for a reset, so without
 * this the banner would sit on a stale "rate limit reached" indefinitely.
 */
export function useActiveRateLimitWindow(input: {
  readonly provider: "codex" | "claudeAgent";
  readonly effectiveAccountId: string | null;
  readonly accounts?: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly rateLimitStatus: ReadonlyArray<ThreadProviderRateLimitStatus> | undefined;
}): RateLimitBannerWindow | undefined {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const status = selectRateLimitBannerWindow({ ...input, nowMs });
  const msUntilReset = nextRateLimitResetMs({ window: status, nowMs });
  useEffect(() => {
    if (msUntilReset === null) return;
    const timer = window.setTimeout(
      () => setNowMs(Date.now()),
      Math.min(msUntilReset + 1_000, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [msUntilReset]);
  return status;
}
